import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/Layout";
import { Megaphone, AlertTriangle, ExternalLink } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return ""; }
};

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/announcements").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <AppLayout>
      <PageHeader subtitle="Event Updates" title="Announcements" />
      <div className="space-y-3 px-4 pt-2" data-testid="announcements-page">
        {loaded && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-card/50 p-6 text-center">
            <Megaphone size={30} className="mx-auto text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground" data-testid="announcements-empty">
              No announcements yet. Check back here for the latest event updates.
            </p>
          </div>
        )}
        {items.map((a, i) => {
          const high = a.priority === "high";
          return (
            <div key={a.id} data-testid={`announcement-item-${a.id}`}
              className={`rounded-xl border p-4 shadow-sm animate-fade-up ${high ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"}`}
              style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}>
              <div className="flex items-start gap-2">
                <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${high ? "bg-destructive/15 text-destructive" : "bg-primary/10 text-primary"}`}>
                  {high ? <AlertTriangle size={16} /> : <Megaphone size={16} />}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg font-bold leading-tight text-primary">{a.title}</h3>
                    {high && <span className="rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Important</span>}
                  </div>
                  {a.created_at && <p className="text-[11px] font-medium text-muted-foreground">{fmt(a.created_at)}</p>}
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-foreground/80">{a.message}</p>
                  {a.link && (
                    <a href={a.link} target="_blank" rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-secondary underline underline-offset-2">
                      <ExternalLink size={12} /> Learn more
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AppLayout>
  );
}
